// src/HotMelt.js — Hot Melt Adhesive Costing Module

import { useState, useEffect, useMemo, useCallback } from "react";
import { nowDubai } from "./utils/dateUtils";
import {
  fetchHmGrades,
  fetchHmCycles,
  fetchHmCycle,
  createHmCycle,
  createHmGrade,
  updateHmGrade,
  updateHmCycle,
  updateHmCycleSettings,
} from "./apiService";

const HM_MARGINS = [10, 12, 15, 20, 25];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Spinner ────────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        border: "3px solid #e5e5e5", borderTopColor: "#4f0da3",
        animation: "spin 0.7s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Shared table styles (mirrors App.js S.th / S.td) ──────────────────────────
const thS = { textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "#440099", borderBottom: "1px solid #ece8f5", background: "#f3eeff", whiteSpace: "nowrap" };
const tdS = { padding: "9px 12px", borderBottom: "1px solid #ece8f5", verticalAlign: "middle", fontSize: 12, color: "#1a0040" };

// ── Modal shell styles ────────────────────────────────────────────────────────
const overlayS = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalS   = { background: "#fff", borderRadius: 14, padding: 24, width: 540, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" };
const lblS     = { display: "flex", flexDirection: "column", gap: 3, fontSize: 12, color: "#555" };
const inpS     = { padding: "5px 8px", borderRadius: 5, border: "1px solid #ddd", fontSize: 13, marginTop: 2 };
const btnPri   = { padding: "8px 20px", background: "#4f0da3", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" };
const btnSec   = { padding: "8px 16px", background: "#f0f0f0", color: "#333", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" };

// ── New Grade Modal ────────────────────────────────────────────────────────────
function NewGradeModal({ grades, onSave, onClose }) {
  const maxSort   = grades.length > 0 ? Math.max(...grades.map(g => g.sortOrder ?? 0)) : 0;
  const [id,          setId]          = useState("");
  const [displayName, setDisplayName] = useState("");
  const [application, setApplication] = useState("");
  const [sortOrder,   setSortOrder]   = useState(maxSort + 1);
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState("");

  const handleSave = async () => {
    if (!id.trim())          { setErr("Grade ID is required.");          return; }
    if (!application.trim()) { setErr("Application description is required."); return; }
    setSaving(true); setErr("");
    try {
      const result = await onSave({
        id: id.trim().toUpperCase(),
        displayName: displayName.trim() || id.trim().toUpperCase(),
        application: application.trim(),
        sortOrder,
      });
      onClose(result);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlayS} onClick={e => { if (e.target === e.currentTarget) onClose(null); }}>
      <div style={{ ...modalS, width: 380 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Add New Grade</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <label style={lblS}>
            Grade Code (internal, immutable)
            <input style={inpS} value={id}
              onChange={e => { const v = e.target.value.toUpperCase(); setId(v); if (!displayName) setDisplayName(v); }}
              placeholder="e.g. 999X" />
          </label>
          <label style={lblS}>
            Display Name (shown to users)
            <input style={inpS} value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g. SBHM 999X" />
          </label>
          <label style={lblS}>
            Application Description
            <input style={inpS} value={application}
              onChange={e => setApplication(e.target.value)}
              placeholder="e.g. Woodworking, Packaging…" />
          </label>
          <label style={lblS}>
            Sort Order
            <input type="number" style={inpS} value={sortOrder}
              onChange={e => setSortOrder(parseInt(e.target.value) || 0)} />
          </label>
        </div>
        {err && <div style={{ color: "red", fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={() => onClose(null)} style={btnSec}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Adding…" : "Add Grade"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Grade Modal ───────────────────────────────────────────────────────────
function EditGradeModal({ grade, onSave, onClose }) {
  const [displayName, setDisplayName] = useState(grade.displayName || grade.id);
  const [application, setApplication] = useState(grade.application || "");
  const [sortOrder,   setSortOrder]   = useState(grade.sortOrder ?? 0);
  const [active,      setActive]      = useState(grade.active ?? true);
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState("");

  const handleSave = async () => {
    if (!displayName.trim()) { setErr("Display name is required."); return; }
    if (!application.trim()) { setErr("Application description is required."); return; }
    setSaving(true); setErr("");
    try {
      const result = await onSave(grade.id, { displayName: displayName.trim(), application: application.trim(), sortOrder, active });
      onClose(result);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlayS} onClick={e => { if (e.target === e.currentTarget) onClose(null); }}>
      <div style={{ ...modalS, width: 400 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Edit Grade</div>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 16 }}>Grade code: <strong>{grade.id}</strong> (immutable)</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <label style={lblS}>
            Display Name
            <input style={inpS} value={displayName} onChange={e => setDisplayName(e.target.value)} />
          </label>
          <label style={lblS}>
            Application Description
            <input style={inpS} value={application} onChange={e => setApplication(e.target.value)} />
          </label>
          <label style={lblS}>
            Sort Order
            <input type="number" style={inpS} value={sortOrder} onChange={e => setSortOrder(parseInt(e.target.value) || 0)} />
          </label>
          <label style={{ ...lblS, flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
            Active
          </label>
        </div>
        {err && <div style={{ color: "red", fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={() => onClose(null)} style={btnSec}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New Cycle Modal ────────────────────────────────────────────────────────────
function NewCycleModal({ grades, latestCycle, onSave, onClose }) {
  const now = nowDubai();
  const [year,         setYear]         = useState(now.year);
  const [month,        setMonth]        = useState(now.month);
  const [fxRate,       setFxRate]       = useState(latestCycle ? Number(latestCycle.fxRate)       : 3.685);
  const [customsPct,   setCustomsPct]   = useState(latestCycle ? Number(latestCycle.customsPct) * 100 : 5);
  const [capitalPct,   setCapitalPct]   = useState(latestCycle ? Number(latestCycle.capitalPct) * 100 : 3);
  const [freightPerKg, setFreightPerKg] = useState(latestCycle ? Number(latestCycle.freightPerKg) : 0);
  const [warSurcharge, setWarSurcharge] = useState(false);
  const [prices,       setPrices]       = useState(() => {
    const m = {};
    grades.forEach(g => {
      const existing = latestCycle?.prices?.find(p => p.gradeId === g.id);
      m[g.id] = existing ? String(Number(existing.usdPrice)) : "0";
    });
    return m;
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const slug  = `${month.toString().padStart(2,"0")}-${(year % 100).toString().padStart(2,"0")}`;
  const label = `${MONTH_NAMES[month - 1]} ${year}`;

  const handleSave = async () => {
    setSaving(true); setErr("");
    try {
      const result = await onSave({
        slug, label,
        periodYear:   year,
        periodMonth:  month,
        warSurcharge,
        fxRate,
        customsPct:   customsPct   / 100,
        capitalPct:   capitalPct   / 100,
        freightPerKg,
        notes:        null,
        publishedOn:  null,
        prices: grades.map(g => ({ gradeId: g.id, usdPrice: parseFloat(prices[g.id]) || 0 })),
      });
      onClose(result);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlayS} onClick={e => { if (e.target === e.currentTarget) onClose(null); }}>
      <div style={modalS}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>New Pricing Cycle</div>

        {/* Period */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
          <label style={lblS}>
            Year
            <input type="number" value={year} min={2020} max={2099}
              onChange={e => setYear(parseInt(e.target.value) || now.year)} style={inpS} />
          </label>
          <label style={lblS}>
            Month
            <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={inpS}>
              {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </label>
        </div>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 14 }}>
          Slug: <strong>{slug}</strong> · Label: <strong>{label}</strong>
        </div>

        {/* Cost settings */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[
            ["FX Rate (AED/USD)", fxRate,       setFxRate,       0.001],
            ["Customs %",         customsPct,   setCustomsPct,   0.1  ],
            ["Capital %",         capitalPct,   setCapitalPct,   0.1  ],
            ["Freight AED/kg",    freightPerKg, setFreightPerKg, 0.001],
          ].map(([lbl, val, set, step]) => (
            <label key={lbl} style={lblS}>
              {lbl}
              <input type="number" step={step} value={val}
                onChange={e => set(parseFloat(e.target.value) || 0)} style={inpS} />
            </label>
          ))}
        </div>
        <label style={{ ...lblS, flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <input type="checkbox" checked={warSurcharge} onChange={e => setWarSurcharge(e.target.checked)} />
          War surcharge
        </label>

        {/* Grade prices */}
        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Grade Prices (USD/kg)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16, maxHeight: 200, overflowY: "auto" }}>
          {grades.map(g => (
            <label key={g.id} style={lblS}>
              {g.displayName || g.id}
              <input type="number" step="0.01" value={prices[g.id] ?? "0"}
                onChange={e => setPrices(p => ({ ...p, [g.id]: e.target.value }))}
                style={inpS} />
            </label>
          ))}
        </div>

        {err && <div style={{ color: "red", fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={() => onClose(null)} style={btnSec}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Creating…" : "Create Cycle"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Price History Tab ──────────────────────────────────────────────────────────
function PriceHistoryTab({ cycles, gradesMap, displayNamesMap }) {
  const [fromSlug,  setFromSlug]  = useState("");
  const [toSlug,    setToSlug]    = useState("");
  const [fromCycle, setFromCycle] = useState(null);
  const [toCycle,   setToCycle]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [err,       setErr]       = useState("");

  const load = useCallback(async () => {
    if (!fromSlug || !toSlug) return;
    setLoading(true); setErr("");
    try {
      const [a, b] = await Promise.all([fetchHmCycle(fromSlug), fetchHmCycle(toSlug)]);
      setFromCycle(a); setToCycle(b);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [fromSlug, toSlug]);

  useEffect(() => { load(); }, [load]); // eslint-disable-line

  const rows = useMemo(() => {
    if (!fromCycle || !toCycle) return [];
    const allIds = new Set([
      ...(fromCycle.prices || []).map(p => p.gradeId),
      ...(toCycle.prices   || []).map(p => p.gradeId),
    ]);
    return [...allIds].map(gradeId => {
      const a = (fromCycle.prices || []).find(p => p.gradeId === gradeId);
      const b = (toCycle.prices   || []).find(p => p.gradeId === gradeId);
      const usdA = a ? Number(a.usdPrice) : null;
      const usdB = b ? Number(b.usdPrice) : null;
      const deltaUsd = usdA != null && usdB != null ? usdB - usdA : null;
      const deltaPct = usdA && deltaUsd != null ? (deltaUsd / usdA) * 100 : null;
      const aed = (cycle, usd) => {
        if (usd == null) return null;
        return usd * Number(cycle.fxRate) * (1 + Number(cycle.customsPct)) * (1 + Number(cycle.capitalPct)) + Number(cycle.freightPerKg);
      };
      const aedA = aed(fromCycle, usdA);
      const aedB = aed(toCycle,   usdB);
      const deltaAed = aedA != null && aedB != null ? aedB - aedA : null;
      return { gradeId, application: gradesMap[gradeId] || "—", usdA, usdB, deltaUsd, deltaPct, deltaAed };
    });
  }, [fromCycle, toCycle, gradesMap]);

  const fmt2 = v => v == null ? "–" : Number(v).toFixed(2);
  const fmtD = v => v == null ? "–" : (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));
  const dc   = v => v == null ? "#888" : v > 0 ? "#c0392b" : v < 0 ? "#27ae60" : "#555";

  return (
    <div>
      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        {[["From", fromSlug, setFromSlug], ["To", toSlug, setToSlug]].map(([label, val, set]) => (
          <label key={label} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
            <span style={{ color: "#888" }}>{label}</span>
            <select value={val} onChange={e => set(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }}>
              <option value="">— select —</option>
              {cycles.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          </label>
        ))}
      </div>
      {loading && <Spinner />}
      {err && <p style={{ color: "red", fontSize: 13 }}>{err}</p>}
      {!loading && fromCycle && toCycle && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                {["Grade", "Application",
                  `USD (${fromCycle.label})`, `USD (${toCycle.label})`,
                  "Δ USD", "Δ %", "Δ AED"].map((h, i) => (
                  <th key={i} style={{ ...thS, textAlign: i < 2 ? "left" : "right" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.gradeId} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ ...tdS, textAlign: "left" }}><strong>{(displayNamesMap && displayNamesMap[r.gradeId]) || r.gradeId}</strong></td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.application}</td>
                  <td style={{ ...tdS, textAlign: "right" }}>{fmt2(r.usdA)}</td>
                  <td style={{ ...tdS, textAlign: "right" }}>{fmt2(r.usdB)}</td>
                  <td style={{ ...tdS, textAlign: "right", color: dc(r.deltaUsd) }}>{fmtD(r.deltaUsd)}</td>
                  <td style={{ ...tdS, textAlign: "right", color: dc(r.deltaPct) }}>
                    {r.deltaPct == null ? "–" : `${r.deltaPct >= 0 ? "+" : ""}${r.deltaPct.toFixed(1)}%`}
                  </td>
                  <td style={{ ...tdS, textAlign: "right", color: dc(r.deltaAed) }}>{fmtD(r.deltaAed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Grade Detail Modal ────────────────────────────────────────────────────────
function GradeDetailModal({ gradeId, displayName, application, usd, live, customMargin, warSurcharge, onClose }) {
  const fmt  = (v, d = 3) => v == null ? "–" : Number(v).toFixed(d);
  const fmt2 = v => fmt(v, 2);

  const usdVal       = parseFloat(usd) || 0;
  const usdInAed     = usdVal * live.fxRate;
  const afterCustoms = usdInAed * (1 + live.customsPct / 100);
  const afterCapital = afterCustoms * (1 + live.capitalPct / 100);
  const aedCost      = afterCapital + live.freightPerKg;

  const steps = [
    [`USD price`,                                          `${fmt(usdVal, 4)} USD/kg`],
    [`× FX rate (${live.fxRate})`,                        `AED ${fmt(usdInAed, 4)}/kg`],
    [`+ Customs ${live.customsPct}%`,                     `AED ${fmt(afterCustoms, 4)}/kg`],
    [`+ Capital cost ${live.capitalPct}%`,                `AED ${fmt(afterCapital, 4)}/kg`],
    [`+ Freight`,                                         `+ AED ${fmt(live.freightPerKg, 3)}/kg`],
  ];

  const margins = [...HM_MARGINS, customMargin];

  return (
    <div style={overlayS} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalS, width: 460 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{displayName || gradeId}</div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{application || "—"}</div>
            {warSurcharge && (
              <div style={{ fontSize: 11, color: "#c0392b", fontWeight: 600, marginTop: 4 }}>⚠ War surcharge applied</div>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>

        {/* Cost build-up */}
        <div style={{ background: "#f8f4ff", border: "1px solid #d0c4f0", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: "#4f0da3", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            Cost Build-up
          </div>
          {steps.map(([label, value], i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between",
              padding: "5px 0",
              borderBottom: i === steps.length - 1 ? "none" : "1px solid #e8e0f8",
              fontSize: 12,
            }}>
              <span style={{ color: "#555" }}>{label}</span>
              <span style={{ fontWeight: i === steps.length - 1 ? 600 : 400 }}>{value}</span>
            </div>
          ))}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginTop: 10, paddingTop: 10, borderTop: "2px solid #4f0da3",
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#4f0da3" }}>AED cost / kg</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#4f0da3" }}>AED {fmt2(aedCost)}</span>
          </div>
        </div>

        {/* Selling prices */}
        <div style={{ fontWeight: 700, fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
          Selling Prices
        </div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(margins.length, 4)}, 1fr)`, gap: 6 }}>
          {margins.map((m, i) => {
            const isCustom = i === margins.length - 1;
            const sp = aedCost * (1 + m / 100);
            return (
              <div key={m} style={{
                background: isCustom ? "#ede9fb" : "#f8faff",
                borderRadius: 7, padding: "8px 10px", textAlign: "center",
                border: `1px solid ${isCustom ? "#c4b5f5" : "#e0e9ff"}`,
              }}>
                <div style={{ fontSize: 10, color: isCustom ? "#4f0da3" : "#888" }}>
                  {m}%{isCustom ? " ★" : ""}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3, color: isCustom ? "#4f0da3" : "#1a0040" }}>
                  {fmt2(sp)}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={btnSec}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function HotMelt() {
  const [grades,         setGrades]         = useState([]);
  const [cycles,         setCycles]         = useState([]);
  const [selectedSlug,   setSelectedSlug]   = useState("");
  const [cycle,          setCycle]          = useState(null);
  const [cycleLoading,   setCycleLoading]   = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tab,            setTab]            = useState("pricing");
  const [hovRow,         setHovRow]         = useState(null);
  const [hovCol,         setHovCol]         = useState(null);
  const [newCycleOpen,   setNewCycleOpen]   = useState(false);
  const [newGradeOpen,   setNewGradeOpen]   = useState(false);

  // Settings draft (FX, customs, capital, freight) — stored as user-facing units (% not fraction)
  const [draft,        setDraft]        = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [saveMsg,      setSaveMsg]      = useState("");

  // Prices draft — editable copy of cycle.prices
  const [draftPrices,   setDraftPrices]   = useState([]);
  const [savingPrices,  setSavingPrices]  = useState(false);
  const [pricesMsg,     setPricesMsg]     = useState("");

  const [customMargin,  setCustomMargin]  = useState(18);
  const [detailGrade,   setDetailGrade]   = useState(null); // { gradeId, usdPrice }
  const [editGrade,     setEditGrade]     = useState(null); // HmGrade object

  const gradesMap = useMemo(() => {
    const m = {};
    for (const g of grades) m[g.id] = g.application;
    return m;
  }, [grades]);

  const displayNamesMap = useMemo(() => {
    const m = {};
    for (const g of grades) m[g.id] = g.displayName || g.id;
    return m;
  }, [grades]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([fetchHmGrades(), fetchHmCycles()])
      .then(([g, c]) => {
        setGrades([...g].sort((a,b) => a.id.localeCompare(b.id)));
        setCycles(c);
        if (c.length > 0) setSelectedSlug(c[c.length - 1].slug);
      })
      .catch(console.error)
      .finally(() => setInitialLoading(false));
  }, []);

  // ── Load selected cycle ───────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedSlug) return;
    setCycleLoading(true);
    fetchHmCycle(selectedSlug)
      .then(c => {
        setCycle(c);
        setDraft({
          fxRate:       Number(c.fxRate       ?? 3.685),
          customsPct:   Number(c.customsPct   ?? 0) * 100,
          capitalPct:   Number(c.capitalPct   ?? 0) * 100,
          freightPerKg: Number(c.freightPerKg ?? 0),
        });
        const cycleGradeIds = new Set((c.prices || []).map(p => p.gradeId));
        const existing = (c.prices || []).map(p => ({ ...p, usdPrice: String(Number(p.usdPrice)) }));
        const missing  = grades.filter(g => !cycleGradeIds.has(g.id)).map(g => ({ gradeId: g.id, usdPrice: "0" }));
        setDraftPrices([...existing, ...missing]);
      })
      .catch(console.error)
      .finally(() => setCycleLoading(false));
  }, [selectedSlug]);

  const live = useMemo(() => draft ?? { fxRate: 3.685, customsPct: 0, capitalPct: 0, freightPerKg: 0 }, [draft]);
  const updateDraft = (key, val) => { setDraft(p => ({ ...p, [key]: val })); setSaveMsg(""); };

  // ── Save cycle settings (FX, customs, capital, freight) ──────────────────
  const saveSettings = async () => {
    setSaving(true); setSaveMsg("");
    try {
      const updated = await updateHmCycleSettings(selectedSlug, {
        label:        cycle.label,
        periodYear:   cycle.periodYear,
        periodMonth:  cycle.periodMonth,
        warSurcharge: cycle.warSurcharge ?? false,
        fxRate:       live.fxRate,
        customsPct:   live.customsPct   / 100,
        capitalPct:   live.capitalPct   / 100,
        freightPerKg: live.freightPerKg,
        notes:        cycle.notes       ?? null,
        publishedOn:  cycle.publishedOn ?? null,
      });
      setCycle(updated);
      setSaveMsg("Saved.");
      setTimeout(() => setSaveMsg(""), 2500);
    } catch (e) { setSaveMsg("Error: " + e.message); }
    finally { setSaving(false); }
  };

  // ── Save grade prices ─────────────────────────────────────────────────────
  const savePrices = async () => {
    setSavingPrices(true); setPricesMsg("");
    try {
      const updated = await updateHmCycle(selectedSlug, {
        slug:         cycle.slug,
        label:        cycle.label,
        periodYear:   cycle.periodYear,
        periodMonth:  cycle.periodMonth,
        warSurcharge: cycle.warSurcharge ?? false,
        fxRate:       live.fxRate,
        customsPct:   live.customsPct   / 100,
        capitalPct:   live.capitalPct   / 100,
        freightPerKg: live.freightPerKg,
        notes:        cycle.notes       ?? null,
        publishedOn:  cycle.publishedOn ?? null,
        prices: draftPrices.map(p => ({ gradeId: p.gradeId, usdPrice: parseFloat(p.usdPrice) || 0 })),
      });
      setCycle(updated);
      setDraftPrices((updated.prices || []).map(p => ({ ...p, usdPrice: String(Number(p.usdPrice)) })));
      setPricesMsg("Saved.");
      setTimeout(() => setPricesMsg(""), 2500);
    } catch (e) { setPricesMsg("Error: " + e.message); }
    finally { setSavingPrices(false); }
  };

  // ── Create new cycle ──────────────────────────────────────────────────────
  const handleCreateCycle = async (body) => {
    const result = await createHmCycle(body);
    const updatedCycles = await fetchHmCycles();
    setCycles(updatedCycles);
    setSelectedSlug(result.slug);
    return result;
  };

  // ── Create new grade ──────────────────────────────────────────────────────
  const handleCreateGrade = async (body) => {
    const result = await createHmGrade(body);
    const updatedGrades = await fetchHmGrades();
    setGrades([...updatedGrades].sort((a, b) => a.id.localeCompare(b.id)));
    setDraftPrices(prev => [...prev, { gradeId: result.id, usdPrice: "0" }]);
    return result;
  };

  // ── Update existing grade ─────────────────────────────────────────────────
  const handleUpdateGrade = async (id, body) => {
    const result = await updateHmGrade(id, body);
    const updatedGrades = await fetchHmGrades();
    setGrades([...updatedGrades].sort((a, b) => a.id.localeCompare(b.id)));
    return result;
  };

  // ── Calc ──────────────────────────────────────────────────────────────────
  const calcAed = usd =>
    usd != null
      ? Number(usd) * live.fxRate * (1 + live.customsPct / 100) * (1 + live.capitalPct / 100) + live.freightPerKg
      : null;
  const calcSp  = (usd, margin) => { const a = calcAed(usd); return a != null ? a * (1 + margin / 100) : null; };
  const fmt     = (v, d = 3)    => v == null ? "–" : Number(v).toFixed(d);

  const CUST_COL = 4 + HM_MARGINS.length;

  if (initialLoading) return <Spinner />;

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Hot Melt Adhesive Costing</div>
          <div className="page-subtitle">Pricing cycles &amp; grade cost matrix</div>
        </div>
        <div className="page-header-right">
          <select value={selectedSlug} onChange={e => setSelectedSlug(e.target.value)}
            className="form-input">
            {cycles.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
          <button className="hm-new-cycle-btn" onClick={() => setNewGradeOpen(true)}
            style={{ background: "#0f766e" }}>
            + New Grade
          </button>
          <button className="hm-new-cycle-btn" onClick={() => setNewCycleOpen(true)}>
            + New Cycle
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="hm-tab-bar">
        {[["pricing", "Pricing"], ["history", "Price History"]].map(([key, label]) => (
          <button key={key} className={`hm-tab${tab === key ? " active" : ""}`}
            onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {tab === "history" && <PriceHistoryTab cycles={cycles} gradesMap={gradesMap} displayNamesMap={displayNamesMap} />}

      {tab === "pricing" && (
        cycleLoading ? <Spinner /> : !cycle ? <p style={{ color: "#888" }}>No cycle data.</p> : (
          <>
            {/* Settings bar */}
            <div className="hm-settings-bar">
              {[
                { label: "FX Rate (AED/USD)", key: "fxRate",       step: 0.001, suffix: ""  },
                { label: "Customs",           key: "customsPct",   step: 0.1,   suffix: "%" },
                { label: "Capital",           key: "capitalPct",   step: 0.1,   suffix: "%" },
                { label: "Freight AED/kg",    key: "freightPerKg", step: 0.001, suffix: ""  },
                { label: "Custom Margin ★",   key: "_custom",      step: 0.5,   suffix: "%" },
              ].map(({ label, key, step, suffix }, i, arr) => (
                <span key={key} style={{ display: "contents" }}>
                  <span className="hm-settings-label">{label}:</span>
                  <input
                    type="number" step={step}
                    defaultValue={key === "_custom" ? customMargin : live[key]}
                    key={key === "_custom" ? customMargin : live[key]}
                    onBlur={e => {
                      const v = parseFloat(e.target.value);
                      if (isNaN(v)) return;
                      if (key === "_custom") setCustomMargin(v);
                      else updateDraft(key, v);
                    }}
                    className={`hm-settings-input${key === "_custom" ? " custom" : ""}`}
                  />
                  <span className="hm-settings-unit">{suffix}</span>
                  {i < arr.length - 1 && <span className="hm-settings-divider" />}
                </span>
              ))}
              <button className="hm-save-btn" onClick={saveSettings} disabled={saving}>
                {saving ? "Saving…" : "Save Settings"}
              </button>
              {saveMsg && <span className={`hm-save-msg ${saveMsg.startsWith("Error") ? "err" : "ok"}`}>{saveMsg}</span>}
            </div>

            {/* Pricing table */}
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ ...thS, textAlign: "left", minWidth: 70 }}>Grade</th>
                    <th style={{ ...thS, textAlign: "left", minWidth: 140 }}>Application</th>
                    <th style={{ ...thS, textAlign: "right" }}>USD/kg</th>
                    <th style={{ ...thS, textAlign: "right" }}>AED/kg</th>
                    {HM_MARGINS.map(m => <th key={m} style={{ ...thS, textAlign: "right" }}>{m}%</th>)}
                    <th style={{ ...thS, textAlign: "right", background: "#ede9fb", color: "#4f0da3" }}>{customMargin}%★</th>
                  </tr>
                </thead>
                <tbody>
                  {draftPrices.length === 0 && (
                    <tr><td colSpan={4 + HM_MARGINS.length + 1}
                      style={{ ...tdS, textAlign: "center", color: "#aaa", padding: "24px 0" }}>
                      No grade prices in this cycle.
                    </td></tr>
                  )}
                  {draftPrices.map((p, rowIdx) => {
                    const usd    = parseFloat(p.usdPrice) || 0;
                    const aed    = calcAed(usd);
                    const isHovR = hovRow === rowIdx;
                    return (
                      <tr key={p.gradeId}
                        onMouseEnter={() => setHovRow(rowIdx)}
                        onMouseLeave={() => { setHovRow(null); setHovCol(null); }}
                        style={{ borderBottom: "1px solid #f0f0f0" }}>

                        {/* Grade — click to open detail; pencil icon to edit grade */}
                        <td style={{ ...tdS, textAlign: "left", fontWeight: 700, background: isHovR ? "#e8f0fe" : "transparent" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span
                              onClick={() => setDetailGrade({ gradeId: p.gradeId, usdPrice: p.usdPrice })}
                              style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline" }}>
                              {displayNamesMap[p.gradeId] || p.gradeId}
                            </span>
                            {isHovR && (
                              <span
                                onClick={() => setEditGrade(grades.find(g => g.id === p.gradeId))}
                                style={{ cursor: "pointer", fontSize: 11, color: "#9ca3af" }}
                                title="Edit grade">
                                ✎
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Application */}
                        <td style={{ ...tdS, textAlign: "left", background: isHovR ? "#e8f0fe" : "transparent" }}>
                          {gradesMap[p.gradeId] || "—"}
                        </td>
                        {/* USD — editable */}
                        {(() => {
                          const colIdx = 2;
                          const isHov  = isHovR || hovCol === colIdx;
                          return (
                            <td style={{ ...tdS, textAlign: "right", background: isHov ? "#e8f0fe" : "transparent", padding: "4px 8px" }}
                              onMouseEnter={() => setHovCol(colIdx)} onMouseLeave={() => setHovCol(null)}>
                              <input
                                type="number" step="0.01"
                                value={p.usdPrice}
                                onChange={e => {
                                  const val = e.target.value;
                                  setDraftPrices(prev => prev.map((x, i) => i === rowIdx ? { ...x, usdPrice: val } : x));
                                  setPricesMsg("");
                                }}
                                style={{
                                  width: 70, padding: "3px 5px", fontSize: 12, borderRadius: 4,
                                  border: "1px solid #d0c4f0", textAlign: "right", background: "#fff",
                                }}
                              />
                            </td>
                          );
                        })()}
                        {/* AED */}
                        {(() => {
                          const colIdx = 3;
                          const isHov  = isHovR || hovCol === colIdx;
                          return (
                            <td style={{ ...tdS, textAlign: "right", fontWeight: 700, background: isHov ? "#dce9fd" : "#f8f8f8" }}
                              onMouseEnter={() => setHovCol(colIdx)} onMouseLeave={() => setHovCol(null)}>
                              {fmt(aed, 3)}
                            </td>
                          );
                        })()}
                        {/* Margin columns */}
                        {HM_MARGINS.map((m, mi) => {
                          const colIdx = 4 + mi;
                          const isHov  = isHovR || hovCol === colIdx;
                          return (
                            <td key={m}
                              style={{ ...tdS, textAlign: "right", background: isHov ? "#dce9fd" : "transparent" }}
                              onMouseEnter={() => setHovCol(colIdx)} onMouseLeave={() => setHovCol(null)}>
                              {fmt(calcSp(usd, m), 3)}
                            </td>
                          );
                        })}
                        {/* Custom margin */}
                        {(() => {
                          const isHov = isHovR || hovCol === CUST_COL;
                          return (
                            <td style={{ ...tdS, textAlign: "right", fontWeight: 600, background: isHov ? "#c7b8f5" : "#ede9fb", color: "#4f0da3" }}
                              onMouseEnter={() => setHovCol(CUST_COL)} onMouseLeave={() => setHovCol(null)}>
                              {fmt(calcSp(usd, customMargin), 3)}
                            </td>
                          );
                        })()}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Save Prices bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              <button className="hm-prices-btn" onClick={savePrices} disabled={savingPrices}>
                {savingPrices ? "Saving…" : "Save Prices"}
              </button>
              {pricesMsg && <span className={`hm-save-msg ${pricesMsg.startsWith("Error") ? "err" : "ok"}`}>{pricesMsg}</span>}
              <p className="hm-formula-note" style={{ marginLeft: "auto" }}>
                AED = USD × {live.fxRate} × (1 + {live.customsPct}% customs) × (1 + {live.capitalPct}% capital) + {live.freightPerKg} freight
              </p>
            </div>
          </>
        )
      )}

      {/* Grade Detail Modal */}
      {detailGrade && (
        <GradeDetailModal
          gradeId={detailGrade.gradeId}
          displayName={displayNamesMap[detailGrade.gradeId]}
          application={gradesMap[detailGrade.gradeId]}
          usd={detailGrade.usdPrice}
          live={live}
          customMargin={customMargin}
          warSurcharge={cycle?.warSurcharge}
          onClose={() => setDetailGrade(null)}
        />
      )}

      {/* Edit Grade Modal */}
      {editGrade && (
        <EditGradeModal
          grade={editGrade}
          onSave={handleUpdateGrade}
          onClose={() => setEditGrade(null)}
        />
      )}

      {/* New Grade Modal */}
      {newGradeOpen && (
        <NewGradeModal
          grades={grades}
          onSave={handleCreateGrade}
          onClose={result => { setNewGradeOpen(false); }}
        />
      )}

      {/* New Cycle Modal */}
      {newCycleOpen && (
        <NewCycleModal
          grades={grades}
          latestCycle={cycle}
          onSave={handleCreateCycle}
          onClose={result => { setNewCycleOpen(false); }}
        />
      )}
    </>
  );
}
